// @flow

import * as React from 'react';
import moment from 'moment';
import smoothscroll from 'smoothscroll-polyfill';

import type { Client as ShowAPIClient } from './../../ShowPage/APIClient';
import type {
  Client as ScheduleAPIClient,
  ScheduleAPIResponse
} from './../APIClient';
import type {Airing, Channel, Episode, Schedule, Show} from "../Objects";
import {getWindow} from './../../Helpers/browsers';
import {getChannelIndex} from "../helpers";

// Polyfill scrolling for MS and Apple browsers
getWindow()
  .then(() => {
    smoothscroll.polyfill();
  })
  .catch(() => {});

export type ScheduleInterval = 15 | 30 | 60;

export type Schedules = Map<Channel, Schedule>;

export type DateParts = {|
  year: number,
  month: number,
  date: number
|};

type Props = {|
  children: Function,
  interval: ScheduleInterval,
  showClient: () => ShowAPIClient,
  scheduleClient: () => ScheduleAPIClient,
  init: {
    date: DateParts,
    data: ScheduleAPIResponse
  },
  initialChannel: Channel
|};

type State = {|
  current: DateParts,
  airings: { [number]: Airing },
  episodes: { [number]: Episode },
  shows: { [number]: Show },
  schedules: Schedules,
  schedulesChunks: Array<number>,
  currentChannel: number,
  calendarMonth: number,
  calendarYear: number
|};

type DefaultProps = {|
  interval: ScheduleInterval
|};

function getFromStorage<T>(storage: { [number]: T }, id: number): T | null {
  if (typeof storage[id] !== 'undefined') {
    return storage[id];
  }

  return null;
}

function sum(numbers: Array<number>): number {
  return numbers.reduce((s: number, n: number) => s + n, 0);
}

export default class Container extends React.Component<Props, State> {
  static defaultProps: DefaultProps;
  firstCellRef: { current: null | React.ElementRef<any> };
  gridRef: { current: null | React.ElementRef<any> };

  constructor(props: Props) {
    super(props);

    this.firstCellRef = React.createRef();
    this.gridRef = React.createRef();

    this.getAiring = this.getAiring.bind(this);
    this.getEpisode = this.getEpisode.bind(this);
    this.getShow = this.getShow.bind(this);
    this.loadNextDay = this.loadNextDay.bind(this);
    this.loadPrevDay = this.loadPrevDay.bind(this);
    this.moveToDate = this.moveToDate.bind(this);
    this.changeChannel = this.changeChannel.bind(this);
    this.onMonthChange = this.onMonthChange.bind(this);

    this.state = this.buildState(
      props.init.date.year,
      props.init.date.month,
      props.init.date.date,
      props.init.data
    );

  }

  componentDidMount(): void {
    this.scrollToChannel(this.state.currentChannel, 'auto');
  }

  getAiring: number => Airing | null;
  getAiring(id: number): Airing | null {
    return getFromStorage(this.state.airings, id);
  }

  getEpisode: number => Episode | null;
  getEpisode(id: number): Episode | null {
    return getFromStorage(this.state.episodes, id);
  }

  getShow: number => Show | null;
  getShow(id: number): Show | null {
    return getFromStorage(this.state.shows, id);
  }

  changeChannel: (number, ?boolean) => void;
  changeChannel(index: number, scroll: boolean = false): void {
    let { currentChannel } = this.state;

    if (index !== currentChannel) {
      this.setState({currentChannel: index});
      if (scroll) {
        this.scrollToChannel(index, 'smooth')
      }
    }
  }

  scrollToChannel(index: number, behavior: 'smooth' | 'auto') {
    if (this.gridRef.current) {
      this.gridRef.current.scrollTo({
        left: (this.gridRef.current.offsetWidth * index - (80 * index) + index),
        behavior
      });
    }
  }

  loadNextDay: () => Promise<void>;
  loadNextDay(): Promise<void> {
    let { year, month, date } = this.state.current;
    let d = new Date(year, month, date);
    let m = moment(d).add(1, 'day');

    let [newYear, newMonth, newDate] = [m.year(), m.month(), m.date()];

    return this.moveToDate(newYear, newMonth, newDate);
  }

  loadPrevDay: () => Promise<void>;
  loadPrevDay(): Promise<void> {
    let { year, month, date } = this.state.current;
    let d = new Date(year, month, date);
    let m = moment(d).subtract(1, 'day');

    let [newYear, newMonth, newDate] = [m.year(), m.month(), m.date()];

    return this.moveToDate(newYear, newMonth, newDate);
  }

  onMonthChange: (number, number) => void;
  onMonthChange(nextMonth: number, nextYear: number): void {
    this.setState( (state, props) => { 
      return {
        calendarMonth: nextMonth,
        calendarYear: nextYear
      }
     });
  }

  /**
   * Possible future usage of trying to make multi-day schedules. Currently the
   * UI doesn't really have a good explanation for how this might work.
   *
   * @param req
   * @param direction
   * @returns {Promise<void>|Promise<void|*>}
   */
  addData(req: Promise<ScheduleAPIResponse>, direction: 'PRE' | 'POST'): Promise<void> {
    return req.then(schedule => {

      this.setState(prevState => {
        let { airings, episodes, shows, schedules, schedulesChunks } = prevState;
        let chunks = schedulesChunks.slice();

        let respLength = schedule.result.length > 0 &&
          typeof schedule.entities.channel[schedule.result[0]] !== 'undefined' ?
          schedule.entities.channel[schedule.result[0]].airings.length : 0;

        if (direction === 'PRE') {
          chunks.unshift(respLength);
          chunks = chunks.slice(3);
        } else {
          chunks.push(respLength);
          chunks = chunks.slice(-3);
        }

        return {
          airings: {...airings, ...schedule.entities.airing},
          episodes: {...episodes, ...schedule.entities.episode},
          shows: {...shows, ...schedule.entities.show},
          schedules: schedule.result.reduce((newSchedules: Schedules, c: Channel) => {
            if (typeof schedule.entities.channel[c] !== 'undefined') {
              if (schedules.has(c)) {
                let id = schedule.entities.channel[c].id;


                let airings = direction === 'PRE' ?
                  // $FlowFixMe - Checked by condition above
                  (schedule.entities.channel[c].airings.concat(schedules.get(c).airings).slice(0, sum(chunks))) :
                  // $FlowFixMe - Checked by condition above
                  schedules.get(c).airings.concat(schedule.entities.channel[c].airings).slice(-sum(chunks));

                newSchedules.set(c, {id, airings});
              } else {
                newSchedules.set(c, schedule.entities.channel[c]);
              }
            }

            return newSchedules;
          }, new Map()),
          schedulesChunks: chunks
        };
      });
    });
  }

  moveToDate: (number, number, number) => Promise<void>;
  moveToDate(year: number, month: number, date: number): Promise<void> {
    let { interval, scheduleClient } = this.props;
    return scheduleClient().getSchedule(year, month + 1, date, interval)
      .then(schedule => {
        this.setState(() => {
          return this.buildState(year, month, date, schedule);
        });
      })
      .then(() => {
        if (this.firstCellRef.current) {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });

          setTimeout(() => {
            if (this.firstCellRef.current) {
              this.firstCellRef.current.focus();
            }
          }, 500);
        }
      });
  }

  buildState(year: number, month: number, date: number, schedule: ScheduleAPIResponse): State {
    return {
      current: { year, month, date },
      airings: schedule.entities.airing,
      episodes: schedule.entities.episode,
      shows: schedule.entities.show,
      schedules: schedule.result.reduce((schedules: Schedules, c: Channel) => {
        if (typeof schedule.entities.channel[c] !== 'undefined') {
          schedules.set(c, schedule.entities.channel[c]);
        }

        return schedules;
      }, new Map()),
      schedulesChunks: [schedule.result.length],
      currentChannel: this.state != null ? this.state.currentChannel : getChannelIndex(this.props.initialChannel),
      calendarMonth: month,
      calendarYear: year
    };
  }

  replaceData(req: Promise<ScheduleAPIResponse>): Promise<void> {
    return req.then(schedule => {
      this.setState((prevState) => {
        return {
          airings: schedule.entities.airing,
          episodes: schedule.entities.episode,
          shows: schedule.entities.show,
          schedules: schedule.result.reduce((schedules: Schedules, c: Channel) => {
            if (typeof schedule.entities.channel[c] !== 'undefined') {
              schedules.set(c, schedule.entities.channel[c]);
            }

            return schedules;
          }, new Map()),
          schedulesChunks: [schedule.result.length]
        };
      });
    });
  }

  render() {
    return this.props.children({
      current: this.state.current,
      schedules: this.state.schedules,
      getAiring: this.getAiring,
      getEpisode: this.getEpisode,
      getShow: this.getShow,
      loadNextDay: this.loadNextDay,
      loadPrevDay: this.loadPrevDay,
      moveToDate: this.moveToDate,
      getShowAPIClient: this.props.showClient,
      firstCellRef: this.firstCellRef,
      gridRef: this.gridRef,
      currentChannel: this.state.currentChannel,
      changeChannel: this.changeChannel,
      onMonthChange: this.onMonthChange,
      calendarMonth: this.state.calendarMonth,
      calendarYear: this.state.calendarYear
    });
  }
}

Container.defaultProps = {
  interval: 30
};